// Auth context for managing authentication state
import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "../services/authService";
import { onUnauthorized } from "../services/api";

const AuthContext = createContext(null);

const decodeToken = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const USER_STORAGE_KEY = "authUser";

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredUser = (user) => {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  const checkAndApplyToken = () => {
    const token = authService.getToken();
    if (token) {
      const payload = decodeToken(token);
      if (payload && payload.exp && payload.exp * 1000 > Date.now()) {
        return true; // token still valid
      }
    }
    // Token gone or expired
    authService.logout();
    writeStoredUser(null);
    return false;
  };

  useEffect(() => {
    const token = authService.getToken();
    if (token) {
      const payload = decodeToken(token);
      const stored = readStoredUser();
      if (payload && payload.exp && payload.exp * 1000 > Date.now()) {
        setUser(
          stored || {
            email: payload.email,
            userId: payload.userId,
            role: payload.role,
          },
        );
      } else {
        authService.logout();
        writeStoredUser(null);
      }
    }
    setLoading(false);

    // Re-check token expiry when the user returns to the tab.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (!checkAndApplyToken()) {
          setUser(null);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Auto-logout when any API call returns 401 (e.g. token revoked server-side).
    onUnauthorized(() => {
      authService.logout();
      writeStoredUser(null);
      setUser(null);
    });

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const nextUser = {
        email: response.email,
        userId: response.userId,
        role: response.role,
      };
      setUser(nextUser);
      writeStoredUser(nextUser);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    authService.logout();
    writeStoredUser(null);
    setUser(null);
  };

  const isAdmin = user?.role === "admin";

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
